import React, { useEffect, useRef, useState } from 'react'
import {
  CircularProgress,
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  Grid,
  InputField,
  SearchIcon,
  useDebouncedCallback
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import { useSearchQuotesLazyQuery } from '@paintscout/api'
import type { Quote, SearchQuotesQuery } from '@paintscout/api'
import { getFeature, getObjectLabels } from '@paintscout/util/builder'
import AddIcon from '@material-ui/icons/Add'
import QuoteResultsList from '../LinkQuoteDialog/QuoteResultsList'
import classNames from 'classnames'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {
      overflow: 'visible'
    },
    dialogPaper: {
      overflow: 'visible',
      minWidth: 740,
      [theme.breakpoints.down('xs')]: {
        minWidth: 'unset'
      }
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    results: {
      top: -6,
      overflowY: 'auto',
      position: 'absolute',
      backgroundColor: theme.palette.background.paper,
      zIndex: 21474899999,
      width: 'calc(100% - 16px)',
      boxShadow: theme.shadows[1]
    },
    noResults: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(2, 0),
      ...theme.typography.overline,
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    newButton: {
      height: '38px',
      [theme.breakpoints.down('sm')]: {
        height: '36px'
      }
    }
  }),
  { name: 'EstimateSearchDialog' }
)

export interface EstimateSearchDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  contactId?: string
  companyDocId?: string
  companyName?: string
  onSelect: (quote: Partial<Quote>) => void | Promise<void>
  onCreateNew: () => void
  onCancel: (event: React.MouseEvent<HTMLElement>) => void
}

function EstimateSearchDialog(props: EstimateSearchDialogProps) {
  const classes = useStyles(props)
  const { onSelect, onCancel, onCreateNew, contactId, companyDocId, companyName, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const estimateLabel = objectLabels.quote.value
  const estimatesLabel = objectLabels.quote.plural
  const multipleInvoicesEnabled = !!getFeature({ options, path: 'quotes.multipleInvoices' })

  const [query, setQuery] = useState('')
  const [selecting, setSelecting] = useState(false)
  const bookmark = useRef<string>()
  const sort = '-quote_date'

  const [search, { loading, data, error }] = useSearchQuotesLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d: SearchQuotesQuery) => {
      const newBookmark = d?.searchQuotes?.bookmark
      if (newBookmark && newBookmark !== bookmark.current) {
        bookmark.current = newBookmark
      }
    }
  })

  const debouncedQuery = useDebouncedCallback(search, 250)

  useEffect(() => {
    if (!debouncedQuery) return

    const scopeFilter = contactId
      ? {
          type: 'or' as const,
          value: [
            { id: 'contact.id', type: 'equal' as const, value: contactId },
            { id: 'secondaryContact.id', type: 'equal' as const, value: contactId }
          ]
        }
      : companyDocId
        ? {
            type: 'or' as const,
            value: [
              { id: 'contact.companyDocId', type: 'equal' as const, value: companyDocId },
              { id: 'secondaryContact.companyDocId', type: 'equal' as const, value: companyDocId },
              ...(companyName ? [{ id: 'contact.company', type: 'equal' as const, value: companyName }] : [])
            ]
          }
        : null

    const noInvoiceFilter = !multipleInvoicesEnabled
      ? [
          { id: 'child_invoice', type: 'exists' as const, not: true },
          { id: 'child_invoices', type: 'exists' as const, not: true }
        ]
      : []

    const filters = [
      { id: 'is_invoice', type: 'equal' as const, not: true, value: true },
      { id: 'trashed', type: 'equal' as const, not: true, value: true },
      { id: 'archived', type: 'equal' as const, not: true, value: true },
      ...(scopeFilter ? [scopeFilter] : []),
      ...noInvoiceFilter
    ]

    debouncedQuery({
      variables: {
        query,
        filters,
        sort
      }
    })
  }, [debouncedQuery, query, contactId, companyDocId, companyName, multipleInvoicesEnabled])

  useEffect(() => {
    if (error) {
      console.error(error)
      enqueueSnackbar(`Unable to search ${estimatesLabel.toLowerCase()}`, { variant: 'error' })
    }
  }, [error, enqueueSnackbar, estimatesLabel])

  // Prevents double-clicking a result from running the async select flow twice
  // (which could create duplicate invoices)
  const handleSelect = async (quote: Partial<Quote>) => {
    if (selecting) return
    setSelecting(true)
    try {
      await onSelect(quote)
    } finally {
      setSelecting(false)
    }
  }

  const quotes = data?.searchQuotes?.quotes ?? []
  const numResults = data?.searchQuotes?.total_rows ?? 0
  const showNoDocFound = numResults === 0 && !loading && data

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  const NoDocFoundText = (
    <Typography variant={'caption'} color={'textSecondary'}>
      No {estimatesLabel.toLowerCase()} without an invoice were found. Click <strong>New</strong> to start a blank
      invoice.
    </Typography>
  )

  return (
    <Dialog {...baseDialogProps} onClose={onCancel} maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2">
          Select {/^[aeiou]/i.test(estimateLabel) ? 'an' : 'a'} {estimateLabel}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs>
            <InputField
              id="estimate-query"
              label={''}
              autoFocus
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search existing ${estimatesLabel.toLowerCase()}`}
              autoComplete={'off'}
              startAdornment={
                <div className={classes.searchAdornment}>
                  {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                </div>
              }
              endAdornment={
                numResults > 0 ? (
                  <Typography variant="subtitle1">
                    {numResults} result{numResults > 1 ? 's' : ''}
                  </Typography>
                ) : null
              }
            />
          </Grid>
          <Grid item xs={'auto'}>
            <Button
              className={classNames(classes.newButton)}
              variant="dialog"
              color="primary"
              icon={AddIcon}
              onClick={onCreateNew}
              disabled={loading || selecting}
            >
              New
            </Button>
          </Grid>

          {quotes.length > 0 && (
            <Grid item xs={12} style={{ position: 'relative' }}>
              <div className={classes.results}>
                <QuoteResultsList results={quotes} disabled={loading || selecting} onQuoteClick={handleSelect} />
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions centeredButton>{showNoDocFound && NoDocFoundText}</DialogActions>
    </Dialog>
  )
}

export default EstimateSearchDialog
